import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Paper, Typography, Chip, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, IconButton, TextField,
  InputAdornment, Pagination, Tooltip, Skeleton, Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { decryptId, encryptId } from "../helper/idCodec.js";
import { API_ENDPOINTS, getData } from "../../services/apiService.js";

// ── Mapping status config ─────────────────────────────────────────────────
const STATUS_CONFIG = {
  All:       { label: "All",       bg: "#F1F5F9", fg: "#475569" },
  Requested: { label: "Requested", bg: "#FEF9C3", fg: "#854D0E" },
  Accepted:  { label: "Accepted",  bg: "#DBEAFE", fg: "#1E40AF" },
  Picked:    { label: "Picked",    bg: "#F3E8FF", fg: "#6D28D9" },
  Delivered: { label: "Delivered", bg: "#DCFCE7", fg: "#15803D" },
  Archived:  { label: "Archived",  bg: "#FEE2E2", fg: "#B91C1C" },
};

// ── Payment status config ─────────────────────────────────────────────────
const PAY_STATUS_CONFIG = {
  paid:      { label: "Paid",      bg: "#DCFCE7", fg: "#15803D" },
  pending:   { label: "Pending",   bg: "#FEF9C3", fg: "#854D0E" },
  failed:    { label: "Failed",    bg: "#FEE2E2", fg: "#B91C1C" },
  cancelled: { label: "Cancelled", bg: "#F1F5F9", fg: "#64748B" },
  expired:   { label: "Expired",   bg: "#F1F5F9", fg: "#64748B" },
};

// ── Payment method labels ─────────────────────────────────────────────────
const PAY_METHOD_LABEL = {
  razorpay:      "Online",
  cashondelivery:"COD",
  cod:           "COD",
  wallet:        "Wallet",
};

const MiniChip = ({ bg, fg, label }) => (
  <Box component="span" sx={{
    display: "inline-flex", alignItems: "center",
    px: "7px", py: "2px", borderRadius: "6px",
    bgcolor: bg, color: fg,
    fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
  }}>
    {label}
  </Box>
);

const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: "#F1F5F9", fg: "#475569" };
  return <MiniChip bg={cfg.bg} fg={cfg.fg} label={cfg.label} />;
};

const PayStatusChip = ({ status }) => {
  if (!status) return <Typography fontSize={11} color="text.disabled">—</Typography>;
  const key = status.toLowerCase();
  const cfg = PAY_STATUS_CONFIG[key] || { label: status, bg: "#F1F5F9", fg: "#64748B" };
  return <MiniChip bg={cfg.bg} fg={cfg.fg} label={cfg.label} />;
};

const FILTERS = ["All", "Requested", "Accepted", "Picked", "Delivered", "Archived"];
const PAGE_SIZE = 15;

// ── Main Component ─────────────────────────────────────────────────────────
const TripPackages = () => {
  const { id } = useParams();
  const tripId = id ? decryptId(id) : "";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [packages, setPackages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);

  // Derive active filter directly from URL — no separate state so tab
  // clicks and back-navigation always stay in sync.
  const activeFilter = searchParams.get("status") || "All";

  // Counts per status for badge pills
  const [statusCounts, setStatusCounts] = useState({});

  const fetchPackages = useCallback(async (filter, pageNum, searchTerm) => {
    if (!tripId) return;
    setLoading(true);
    setPackages([]);
    setTotalCount(0);
    try {
      const params = {
        trip_id: tripId,
        page: pageNum,
        limit: PAGE_SIZE,
        ...(filter !== "All" && { status: filter }),
        ...(searchTerm && { search: searchTerm }),
      };
      // Use admin endpoint — includes Archived packages, returns statusCounts
      const data = await getData(API_ENDPOINTS.adminTripPackages, params);
      if (data?.statusCode === 200) {
        setPackages(data.result?.packages || []);
        setTotalCount(data.result?.total ?? 0);
        // Sync status counts whenever the All-tab loads (statusCounts come in every response)
        if (data.result?.statusCounts) {
          setStatusCounts(data.result.statusCounts);
        }
      } else {
        setPackages([]);
        setTotalCount(0);
      }
    } catch (e) {
      console.error("Failed to fetch trip packages", e);
      setPackages([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  // Counts now come from the new endpoint's statusCounts field —
  // no separate parallel requests needed.
  useEffect(() => {
    // Prime the counts on mount via an "All" fetch
    fetchPackages("All", 1, "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  // Re-fetch whenever the URL filter, page, or search changes
  useEffect(() => {
    fetchPackages(activeFilter, page, search);
  }, [activeFilter, page, search, fetchPackages]);

  const handleFilterChange = (f) => {
    setPage(1);
    setSearchParams(f !== "All" ? { status: f } : {});
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleViewPackage = (pkgId) => {
    navigate(`/app/package-info/${encryptId(pkgId)}`);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "background.default", minHeight: "100vh" }}>

      {/* ── Header ── */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ bgcolor: "white", border: "1px solid", borderColor: "divider", borderRadius: "10px" }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">
            Trip Packages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Trip ID: {tripId} · {statusCounts["All"] ?? totalCount} package{(statusCounts["All"] ?? totalCount) !== 1 ? "s" : ""} total
          </Typography>
        </Box>
      </Box>

      {/* ── Status filter tabs ── */}
      <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 2.5, display: "flex", gap: 1, flexWrap: "wrap", borderRadius: "14px" }}
      >
        {FILTERS.map((f) => {
          const cfg = STATUS_CONFIG[f];
          const isActive = activeFilter === f;
          const count = statusCounts[f] ?? "";
          return (
            <Box
              key={f}
              onClick={() => handleFilterChange(f)}
              sx={{
                display: "flex", alignItems: "center", gap: 0.8,
                px: 1.8, py: 0.8, borderRadius: "10px", cursor: "pointer",
                fontWeight: isActive ? 700 : 500,
                fontSize: 13,
                bgcolor: isActive ? cfg.bg : "transparent",
                color: isActive ? cfg.fg : "text.secondary",
                border: "1.5px solid",
                borderColor: isActive ? cfg.fg + "44" : "transparent",
                transition: "all 0.15s",
                userSelect: "none",
                "&:hover": { bgcolor: cfg.bg, color: cfg.fg },
              }}
            >
              {f}
              {count !== "" && (
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    minWidth: 18, height: 18, borderRadius: "6px", fontSize: 11, fontWeight: 700,
                    bgcolor: isActive ? cfg.fg : "#E2E8F0",
                    color: isActive ? "#fff" : "#64748B",
                    px: "4px",
                  }}
                >
                  {count}
                </Box>
              )}
            </Box>
          );
        })}

        {/* Search — pushed right */}
        <Box component="form" onSubmit={handleSearchSubmit} sx={{ ml: "auto" }}>
          <TextField
            size="small"
            placeholder="Search tracking code…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onBlur={() => { setSearch(searchInput); setPage(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: "10px", fontSize: 13, bgcolor: "white" },
            }}
            sx={{ width: 220 }}
          />
        </Box>
      </Paper>

      {/* ── Table ── */}
      <Paper variant="outlined" sx={{ borderRadius: "14px", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Tracking Code", "Package", "Sender → Receiver", "Route", "Weight / Space", "Pay Method", "Pay Mode", "Pay Status", "Pkg Status", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: "text.secondary", py: 1.5, whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 10 }).map((__, j) => (
                        <TableCell key={j}><Skeleton height={20} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : packages.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <LocalShippingIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                      <Typography color="text.secondary" fontSize={14}>
                        No packages found{activeFilter !== "All" ? ` with status "${activeFilter}"` : ""}.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
                : packages.map((pkg) => {
                    const from = pkg.pickup_point?.city || pkg.pickup_point?.point || "—";
                    const to   = pkg.drop_point?.city   || pkg.drop_point?.point   || "—";
                    const weight = pkg.package_weight ? `${pkg.package_weight} kg` : "—";
                    const space  = pkg.package_space  ? `${pkg.package_space} m³`  : "—";
                    const pay = pkg.payment || {};
                    const methodKey = (pay.payment_method || "").toLowerCase();
                    const methodLabel = PAY_METHOD_LABEL[methodKey] || (pay.payment_method ? pay.payment_method : "—");
                    const modeLabel = pay.payment_mode === "installment" ? "Installment" : pay.payment_mode === "single" ? "Full" : "—";
                    const modeColor = pay.payment_mode === "installment" ? "#7C3AED" : pay.payment_mode === "single" ? "#0369A1" : "#94A3B8";
                    const modeBg   = pay.payment_mode === "installment" ? "#EDE9FE" : pay.payment_mode === "single" ? "#E0F2FE" : "#F1F5F9";
                    return (
                      <TableRow
                        key={pkg.mapping_id}
                        hover
                        sx={{ "&:last-child td": { border: 0 }, cursor: "pointer" }}
                        onClick={() => handleViewPackage(pkg.package_id)}
                      >
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: "primary.main" }}>
                          {pkg.tracking_code || "—"}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160 }}>
                          <Typography fontSize={13} fontWeight={600} noWrap>
                            {pkg.package_name || `Package #${pkg.package_id}`}
                          </Typography>
                          <Typography fontSize={11} color="text.secondary">
                            ID: {pkg.package_id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Typography fontSize={12} noWrap>
                            {pkg.package_sending_person || "—"}
                          </Typography>
                          <Typography fontSize={11} color="text.secondary" noWrap>
                            → {pkg.package_receiving_person || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={12} fontWeight={500} noWrap>
                            {from} → {to}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={12}>{weight}</Typography>
                          <Typography fontSize={11} color="text.secondary">{space}</Typography>
                        </TableCell>
                        {/* Pay Method */}
                        <TableCell>
                          <Typography fontSize={12} fontWeight={500}>
                            {methodLabel}
                          </Typography>
                        </TableCell>
                        {/* Pay Mode */}
                        <TableCell>
                          {modeLabel !== "—"
                            ? <MiniChip bg={modeBg} fg={modeColor} label={modeLabel} />
                            : <Typography fontSize={11} color="text.disabled">—</Typography>
                          }
                        </TableCell>
                        {/* Pay Status */}
                        <TableCell>
                          <PayStatusChip status={pay.status} />
                        </TableCell>
                        {/* Pkg Status */}
                        <TableCell>
                          <StatusChip status={pkg.status} />
                        </TableCell>
                        <TableCell padding="none">
                          <Tooltip title="View package">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleViewPackage(pkg.package_id); }}
                              sx={{ color: "text.secondary" }}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
              }
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1.5}
            sx={{ borderTop: "1px solid", borderColor: "divider" }}
          >
            <Typography fontSize={13} color="text.secondary">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, totalCount)}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
            </Typography>
            <Stack spacing={1}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                size="small"
                color="primary"
                shape="rounded"
              />
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TripPackages;
